import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorFallback } from '@/components/ErrorFallback';
import { Typography } from '@/components/Typography';
import { VeraLoader } from '@/components/VeraLoader';
import { usePlatform } from '@/data/hooks/api/usePlatform';

interface PlatformGuardProps {
  children: React.ReactNode;
  requiredPlatform: string;
  redirectTo?: string;
}

export const PlatformGuard: React.FC<PlatformGuardProps> = ({
  children,
  requiredPlatform,
}) => {
  const { t } = useTranslation();
  const { hasPlatform, isLoading } = usePlatform();

  // * Show loading state while platforms are being loaded
  if (isLoading) {
    return <VeraLoader fullPage />;
  }

  // * Check if user has access to the required platform
  const hasAccess = hasPlatform(requiredPlatform);

  if (!hasAccess) {
    return (
      <ErrorFallback
        title={t('platform.access_denied.title')}
        description={t('platform.access_denied.message', {
          platform: requiredPlatform,
        })}
        details={
          <Typography variant='paragraphMini'>{t('platform.access_denied.contact')}</Typography>
        }
      />
    );
  }

  return <>{children}</>;
};
